import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Languages } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { useFetch } from "../../lib/useFetch";
import { api, errMsg } from "../../lib/api";
import { FormGrid } from "./FormField";
import { AdminH } from "./AdminLayout";
import { CONFIGS } from "./adminConfigs";

export default function CrudPage({ resource }) {
  const cfg = CONFIGS[resource];
  const { data, reload } = useFetch(`/admin/${resource}`);
  const [edit, setEdit] = useState(null);

  const save = async () => {
    try {
      if (edit.id) await api.put(`/admin/${resource}/${edit.id}`, edit);
      else await api.post(`/admin/${resource}`, edit);
      toast.success("Saved");
      setEdit(null);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const del = async (row) => {
    if (!window.confirm(`Delete "${row[cfg.titleKey || "name"]}"?`)) return;
    try { await api.delete(`/admin/${resource}/${row.id}`); } catch (e) { toast.error(errMsg(e)); }
    reload();
  };
  const translate = async (row) => {
    const id = toast.loading("Translating with AI…");
    try {
      await api.post(`/admin/translate/${resource}/${row.id}`);
      toast.success("IT/ES translations generated – review them in Translations", { id });
      reload();
    } catch (e) {
      toast.error(errMsg(e), { id });
    }
  };

  return (
    <div data-testid={`admin-crud-${resource}`}>
      <AdminH title={cfg.title} sub={cfg.sub}>
        <button onClick={() => setEdit({ ...(cfg.defaults || {}) })} className="btn-buoy !py-2.5" data-testid={`${resource}-add-button`}><Plus size={16} />Add</button>
      </AdminH>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr>{cfg.columns.map(([k, l]) => <th key={k} className="px-4 py-3">{l}</th>)}<th className="px-4 py-3" /></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(data || []).map((row) => (
              <tr key={row.id} className="hover:bg-slate-50" data-testid={`${resource}-row-${row.id}`}>
                {cfg.columns.map(([k, , render]) => <td key={k} className="px-4 py-3 text-slate-700">{render ? render(row) : String(row[k] ?? "")}</td>)}
                <td className="whitespace-nowrap px-4 py-3 text-right">
                  {cfg.translatable && <button onClick={() => translate(row)} className="mr-3 text-slate-400 hover:text-[#00A8B5]" title="AI translate" data-testid={`${resource}-translate-${row.id}`}><Languages size={16} /></button>}
                  <button onClick={() => setEdit({ ...row })} className="mr-3 text-slate-400 hover:text-[#0A2540]" data-testid={`${resource}-edit-${row.id}`}><Pencil size={16} /></button>
                  <button onClick={() => del(row)} className="text-slate-400 hover:text-rose-500" data-testid={`${resource}-delete-${row.id}`}><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && !data.length && <p className="p-8 text-center text-slate-500">No items yet.</p>}
      </div>
      <Sheet open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-2xl" data-testid={`${resource}-form`}>
          <SheetHeader><SheetTitle className="font-display text-2xl">{edit?.id ? "Edit" : "New"} {cfg.singular}</SheetTitle></SheetHeader>
          {edit && <div className="mt-6 space-y-6"><FormGrid fields={cfg.fields} f={edit} setF={setEdit} /><button onClick={save} className="btn-buoy" data-testid={`${resource}-save-button`}>Save</button></div>}
        </SheetContent>
      </Sheet>
    </div>
  );
}
